import {
  uppercaseFirstLetter,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterHourMinute,
  SidePanelDS,
  TextCapitalized,
  Spacer,
  RemoveRedEyeOutlined,
  ButtonDS,
  DownloadOutlined,
} from "@qivia/ui";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { useAppSelector, useAppDispatch } from "../../../../redux/hooks";
import { selectListAllVehiclesInDb } from "../../vehicles/vehiclesSlice";
import { DriversListType } from "../driversAPI";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { selectDriversHistoryList } from "../../fleet/fleetSlice";

export const VehicleSection = (props: {
  driver: DriversListType;
  firstPath: string;
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const driversHistoryList = useAppSelector(selectDriversHistoryList);
  const dispatch = useAppDispatch();
  const pageName = "drivers";

  const vehicle = vehicles.find((v) => v.uuid === props.driver.vehicleUuid);

  const driverHistory = useMemo(
    () =>
      driversHistoryList
        .filter((dh) => dh.driverUuid === props.driver.uuid)
        .map((dh) => {
          const vehicle = vehicles.find((v) => v.uuid === dh.vehicleUuid);
          return {
            ...dh,
            vehicle: vehicle,
          };
        }),
    [driversHistoryList, props.driver.uuid, vehicles],
  );

  const handleClickVehicle = useCallback(() => {
    navigate(
      `/${props.firstPath}/vehicles/${props.driver.vehicleUuid}?fromNavigate=true`,
    );
  }, [navigate, props.firstPath, props.driver.vehicleUuid]);

  const driversHistoryExcelColumns = useMemo(
    () => [
      "registrationNumber",
      "vehicleReference",
      "startOfUsage",
      "startOfUsageTime",
      "endOfUsage",
      "endOfUsageTime",
    ],
    [],
  );

  const handleDownloadDriverHistory = useCallback(() => {
    if (driverHistory.length <= 0) return;
    const headerTitles = driversHistoryExcelColumns.map((key) =>
      uppercaseFirstLetter(t(`driversHistory.column.${key}`)),
    );

    const bodyList = driverHistory.map((dh) => [
      dh.vehicle?.registrationNumber ?? "",
      dh.vehicle?.reference ?? "",
      dateFormatterDayMonthYearLong2Digits(new Date(dh.startOfUsage)),
      dateFormatterHourMinute(new Date(dh.startOfUsage)),
      dh.endOfUsage
        ? dateFormatterDayMonthYearLong2Digits(new Date(dh.endOfUsage))
        : "",
      dh.endOfUsage ? dateFormatterHourMinute(new Date(dh.endOfUsage)) : "",
    ]);

    const file = createFileXlsx({
      tabTitle: t(`${pageName}.view.title`),
      headerTitles,
      body: bodyList,
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName:
          "QIVIA_" +
          t("drivers.sidepanel.driversHistory.excel.title", {
            firstName: props.driver.name.split(" ")[0],
            lastName: props.driver.name.split(" ")[1],
          }) +
          ".xlsx",
      }),
    );
  }, [dispatch, driversHistoryExcelColumns, driverHistory, t, props.driver]);

  if (!vehicle) {
    return;
  }

  return (
    <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
      <StyledTitle>
        <TextCapitalized>
          {t("drivers.sidePanel.driversHistory")}
        </TextCapitalized>
      </StyledTitle>
      <>
        <Spacer y={2} />
        <SidePanelDS.ActionBlock>
          <SidePanelDS.SectionedActionStripe
            left={
              <div>
                {vehicle.brand && vehicle.model && (
                  <>
                    <TextCapitalized>
                      <StyledRow>
                        <StyledBodyM>{vehicle.brand}</StyledBodyM>
                        <Spacer x={0.5} />
                        <StyledBodyS>{vehicle.model}</StyledBodyS>
                      </StyledRow>
                    </TextCapitalized>
                    <Spacer y={0.5} />
                  </>
                )}
                <StyledBodyM>{props.driver.registrationNumber}</StyledBodyM>
              </div>
            }
            right={
              <StyledLittleIcon>
                <RemoveRedEyeOutlined
                  key="vehicle"
                  onClick={handleClickVehicle}
                />
                <Spacer x={1} />
              </StyledLittleIcon>
            }
          />
        </SidePanelDS.ActionBlock>
        <Spacer y={1} />
        {driverHistory.length > 0 && (
          <ButtonDS
            sizeButton="S"
            format="hug"
            buttonType="secondary"
            text={t("drivers.sidepanel.downloadDriversHistoryList")}
            leftIcon={<DownloadOutlined />}
            onClick={handleDownloadDriverHistory}
          />
        )}
      </>
    </SidePanelDS.Section>
  );
};

const StyledTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledBodyM = styled.div`
  ${typographies["Body/M"]}
`;

const StyledBodyS = styled.div`
  ${typographies["Body/S"]}
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;
